import type {
  SpecsCatalog,
  SpecsCatalogFamily,
  SpecsCatalogFamilyVersion,
  SpecsCatalogProduct,
  SpecsModelSnapshot,
} from "./types";

export type { SpecsCatalog } from "./types";

function cloneValue<T>(value: T): T {
  return JSON.parse(JSON.stringify(value));
}

function readProducts(specs: SpecsCatalog): SpecsCatalogProduct[] {
  if (!Array.isArray(specs.products)) {
    throw new Error("Specs catalog is missing a products array");
  }

  return specs.products;
}

function readFamilies(specs: SpecsCatalog): SpecsCatalogFamily[] {
  if (!Array.isArray(specs.families)) {
    throw new Error("Specs catalog is missing a families array");
  }

  return specs.families;
}

function readFamilyVersions(
  family: SpecsCatalogFamily
): SpecsCatalogFamilyVersion[] {
  return Array.isArray(family.versions) ? family.versions : [];
}

function createDefaultFamilyVersion(
  productName: string
): SpecsCatalogFamilyVersion {
  return {
    name: "1.0",
    products: [{ name: productName }],
  };
}

function findVersionContainingProduct(
  versions: SpecsCatalogFamilyVersion[],
  productName: string
) {
  return versions.findIndex((version) =>
    Array.isArray(version.products)
      ? version.products.some((product) => product.name === productName)
      : false
  );
}

export function extractProductSnapshotFromCatalog(
  specs: SpecsCatalog,
  productName: string,
  versionName: string
): SpecsModelSnapshot | null {
  const product = readProducts(specs).find(
    (candidate) => candidate.name === productName
  );

  if (!product) {
    return null;
  }

  const versions = Array.isArray(product.versions) ? product.versions : [];
  const version = versions.find((candidate) => candidate.name === versionName);

  if (!version) {
    return null;
  }

  return {
    ...cloneValue(product),
    name: product.name ?? "",
    versions,
  } as SpecsModelSnapshot;
}

export function cloneSpecsCatalog(currentSpecs: SpecsCatalog) {
  const nextSpecs = cloneValue(currentSpecs);

  if (!Array.isArray(nextSpecs.products)) {
    throw new Error("Cannot upsert product into specs without products");
  }

  if (!Array.isArray(nextSpecs.families)) {
    throw new Error("Cannot upsert product into specs without families");
  }

  return nextSpecs;
}

export function upsertProductSnapshotIntoCatalog(
  currentSpecs: SpecsCatalog,
  snapshot: SpecsModelSnapshot,
  familyName?: string
) {
  const nextSpecs = cloneSpecsCatalog(currentSpecs);
  const nextProduct = cloneValue(snapshot) as SpecsCatalogProduct;
  const productName = String(nextProduct.name ?? "");
  const products = nextSpecs.products as SpecsCatalogProduct[];
  const families = nextSpecs.families as SpecsCatalogFamily[];

  const productIndex = products.findIndex(
    (product) => product.name === productName
  );
  if (productIndex >= 0) {
    products[productIndex] = nextProduct;
  } else {
    products.push(nextProduct);
  }

  if (familyName === undefined) {
    return nextSpecs;
  }

  if (familyName.trim().length === 0) {
    throw new Error("Cannot upsert product into an unnamed family");
  }

  const familyIndex = families.findIndex(
    (family) => family.name === familyName
  );

  if (familyIndex === -1) {
    families.push({
      name: familyName,
      versions: [createDefaultFamilyVersion(productName)],
    });
    return nextSpecs;
  }

  const family = families[familyIndex];
  const familyVersions = readFamilyVersions(family);
  const familyVersionIndex = findVersionContainingProduct(
    familyVersions,
    productName
  );

  if (familyVersionIndex >= 0) {
    const existingVersion = familyVersions[familyVersionIndex];
    const existingProducts = Array.isArray(existingVersion.products)
      ? existingVersion.products
      : [];
    const alreadyLinked = existingProducts.some(
      (product) => product.name === productName
    );

    if (!alreadyLinked) {
      familyVersions[familyVersionIndex] = {
        ...existingVersion,
        products: [...existingProducts, { name: productName }],
      };
    }
  } else if (familyVersions.length > 0) {
    const lastVersion = familyVersions[familyVersions.length - 1];
    const existingProducts = Array.isArray(lastVersion.products)
      ? lastVersion.products
      : [];

    familyVersions[familyVersions.length - 1] = {
      ...lastVersion,
      products: [...existingProducts, { name: productName }],
    };
  } else {
    familyVersions.push(createDefaultFamilyVersion(productName));
  }

  family.versions = familyVersions;
  families[familyIndex] = family;

  return nextSpecs;
}
